// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { CheckCircle2, Lock, MessageCircle } from 'lucide-react';

const SITE_URL = 'https://jwdonateaudit.com.br';

const VARIANTS = {
  closeMonth: {
    title: 'Fechamento de Mês',
    Icon: MessageCircle,
    iconColor: 'text-green-600',
    box: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
    label: 'text-green-800 dark:text-green-200',
    text: 'text-green-700 dark:text-green-300',
    hint: 'text-green-600 dark:text-green-400',
  },
  closeTrimester: {
    title: 'Fechamento de Trimestre',
    Icon: Lock,
    iconColor: 'text-amber-500',
    box: 'bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800',
    label: 'text-amber-800 dark:text-amber-200',
    text: 'text-amber-700 dark:text-amber-300',
    hint: 'text-amber-600 dark:text-amber-400',
  },
  completed: {
    title: 'Relatório Concluído!',
    Icon: CheckCircle2,
    iconColor: 'text-emerald-500',
    box: 'bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800',
    label: 'text-emerald-800 dark:text-emerald-200',
    text: 'text-emerald-700 dark:text-emerald-300',
    hint: 'text-emerald-600 dark:text-emerald-400',
  },
};

function getVariantKey(info) {
  if (info?.isCloseMonth) return 'closeMonth';
  if (info?.isCloseTrimester) return 'closeTrimester';
  return 'completed';
}

function buildMessage(info) {
  if (info.isCloseMonth) {
    return (
      `*JW Audit - Fechamento de Mês*\n\n` +
      `Área: ${info.formType}\n` +
      `Mês: ${info.monthName}\n` +
      `Período: ${info.title}\n` +
      `Data: ${info.date}\n\n` +
      `Os lançamentos do mês estão fechados e já podem ser vistoriados.\n\n` +
      `Acesse o sistema:\n${SITE_URL}`
    );
  }
  if (info.isCloseTrimester) {
    return (
      `*JW Audit - Fechamento de Trimestre*\n\n` +
      `Área: ${info.formType}\n` +
      `Fechamento do ${info.title}\n` +
      `Data: ${info.date}\n\n` +
      `Pronto para auditoria.\n\nAcesse o sistema:\n${SITE_URL}`
    );
  }
  return (
    `*JW Audit - Relatório Concluído*\n\n` +
    `Formulário: ${info.formType}\n` +
    `Relatório: ${info.title}\n` +
    `Data de conclusão: ${info.date}\n\n` +
    `O relatório de auditoria foi concluído com sucesso.\n\n` +
    `Acesse o sistema:\n${SITE_URL}`
  );
}

function buildDescription(info) {
  if (info.isCloseMonth) {
    return `Os lançamentos de "${info.monthName}" estão fechados. Deseja notificar pelo WhatsApp?`;
  }
  if (info.isCloseTrimester) {
    return `O relatório "${info.title}" é de trimestre. Deseja enviar a notificação de fechamento via WhatsApp?`;
  }
  return 'Deseja notificar a equipe pelo WhatsApp?';
}

function buildSubtitle(info) {
  if (info.isCloseMonth) return `Mês: ${info.monthName} | ${info.title}`;
  if (info.isCloseTrimester) return `Fechamento do ${info.title}`;
  return info.title;
}

function buildDateLabel(info) {
  if (info.isCloseMonth) return `Lançamentos fechados - Pronto para vistoria - ${info.date}`;
  if (info.isCloseTrimester) return `Pronto para auditoria - ${info.date}`;
  return `Concluído em ${info.date}`;
}

export default function WhatsAppNotifyDialog({ open, onOpenChange, info, onClose }) {
  const handleSend = () => {
    if (!info) return;
    const encoded = encodeURIComponent(buildMessage(info));
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
    onClose();
  };

  if (!info) {
    return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent /></Dialog>;
  }

  const variant = VARIANTS[getVariantKey(info)];
  const { Icon } = variant;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="whatsapp-notify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${variant.iconColor}`} />
            {variant.title}
          </DialogTitle>
          <DialogDescription>{buildDescription(info)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className={`border rounded-lg p-3 ${variant.box}`}>
            <p className={`text-sm font-semibold ${variant.label}`}>{info.formType}</p>
            <p className={`text-sm ${variant.text}`}>{buildSubtitle(info)}</p>
            <p className={`text-xs mt-1 ${variant.hint}`}>{buildDateLabel(info)}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Ao clicar em "Enviar pelo WhatsApp", você poderá escolher o contato ou grupo
            para enviar a notificação.
          </p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleSend}
              className="bg-[#25D366] hover:bg-[#20BD5A] text-white w-full"
              data-testid="whatsapp-send-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Enviar pelo WhatsApp
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="whatsapp-skip-btn">
              Pular
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
